import { FC, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import moment from 'moment';
import { gql, useApolloClient } from '@apollo/client';
import { Spacer } from 'jbc-front/components/presenters/Spacer';
import { Required } from 'jbc-front/components/presenters/form/Label';
import { ErrorMessage } from 'jbc-front/components/presenters/form/ErrorMessage';
import { Headding, Item, Panel } from '@/components/PrimaryPanel';
import { DateField } from '@/components/react-hook-form/DateField';
import type { Schema } from '../../schema';

const LMS_PERSONNEL_HISTORY_AS_OF = gql`
  query lmsPersonnelHistoryAsOf($personalId: PersonalId!, $dateOn: Date!) {
    client {
      employee(personalId: $personalId) {
        personnelHistoryAsOf(dateOn: $dateOn) {
          id
          dateOn
          officeId
        }
      }
    }
  }
`;

interface LmsPersonnelHistoryAsOfQueryVariables {
  personalId: string;
  dateOn: string;
}

interface LmsPersonnelHistoryAsOfQueryResult {
  client: {
    employee: {
      personnelHistoryAsOf?: {
        id: string;
        dateOn: string;
        officeId: string;
      };
    };
  };
}

interface DateOnProps {
  personalId: string;
}

export const DateOn: FC<DateOnProps> = ({ personalId }) => {
  const apolloClient = useApolloClient();
  const {
    watch,
    getValues,
    setValue,
    formState: { errors }
  } = useFormContext<Schema>();

  useEffect(() => {
    const subscription = watch(async (data, { name }) => {
      if (name !== 'dateOn' || !data.dateOn) return;
      if (data.officeId) return; // 適用事業所入力済の場合は何もしない

      const dateOn = moment(data.dateOn, 'YYYY/MM/DD');
      if (!dateOn.isValid()) return;

      const res = await apolloClient.query<LmsPersonnelHistoryAsOfQueryResult, LmsPersonnelHistoryAsOfQueryVariables>({
        query: LMS_PERSONNEL_HISTORY_AS_OF,
        variables: {
          personalId,
          dateOn: dateOn.format('YYYY/MM/DD')
        },
        fetchPolicy: 'no-cache'
      });
      const officeId = res.data.client.employee?.personnelHistoryAsOf?.officeId;
      if (!officeId) return;

      if (getValues('officeId')) return; // 通信中に適用事業所が入力された場合はセットしない
      setValue('officeId', `${officeId}`);
    });
    return () => subscription.unsubscribe();
  }, [personalId, apolloClient, watch, getValues, setValue]);

  return (
    <Panel>
      <Headding>
        実施年月日
        <Spacer direction="x" size={8} />
        <Required />
      </Headding>
      <Item>
        <DateField name="dateOn" />
        {errors.dateOn?.message && <ErrorMessage>{errors.dateOn.message}</ErrorMessage>}
      </Item>
    </Panel>
  );
};
